import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from 'react-router-dom';
import {
  isTokenExpired,
  removeToken,
  getToken,
  decodeToken,
} from './utils/TokenUtils';

import Navbar from './components/Navbar';
import Home from './pages/Home';
import Login from './pages/users/Login';
import ForgotPassword from './pages/users/ForgotPassword';
import ResetPassword from './pages/users/ResetPassword';

import Report from './pages/sys/Report';

import PrintTableWithHeader from './components/PrintReport';
import Footer from './components/Footer';
import Charts from './components/Charts';
import ReportForm from './pages/sys/ReportForm';
import UserPermission from './pages/users/UserPermission';

import PortalForm from './pages/sys/PortalForm';
import InvoicePage from './pages/sys/Invoice';
import UserProfile from './pages/users/UserProfile';
import SQLQueryPage from './pages/sys/SqlQueryPage';
import Documentation from './pages/sys/Documentation';
import BalanceSheet from './pages/sys/Accounting/BalanceSheet';
import SysFeatures from './pages/sys/SysFeatures';
import MultiEntryForm from './pages/sys/MultiEntryForm';
import CSVUploader from './pages/sys/CSVUploader';
import VerifyEmail from './pages/users/VerifyEmail';
import Register2Fa from './pages/users/Register2FA';
import Check2FA from './pages/users/Check2FA';
import IncomeStatement from './pages/sys/Accounting/IncomeStatement';
import { updateFavicon } from './utlis';
import { API_URL } from './constants/api';
import LoginDocumentation from './pages/users/LoginDocumentation';
import PortalPromo from './pages/custom/PortalPromo';
import SqlViewer from './pages/sys/SqlViewer';
import ActivityReport from './pages/sys/ActivityReport';
import SqlHome from './pages/sql/Home';
import ApiDocumentation from './pages/sys/ApiDocumentation';
import socket from './utils/socket';
import KaafiPayWeb from './pages/web/KaafiPay';
import ElectionPage from './pages/custom/Election';
import ElectionResults from './pages/custom/ElectionResult';

function StartupRedirect() {
  const hostname = window.location.hostname; // e.g., 'customer.example.com'
  const parts = hostname.split('.'); // e.g., ['customer', 'example', 'com']
  console.log('parts:', parts);
  if (parts.length <= 2) {
    // Example: localhost or example.com
    return <Navigate to="/web" replace />;
  }

  const subdomain = parts[0];

  if (subdomain === 'customer' || subdomain === 'portal') {
    return <Navigate to="/login/customer-login" replace />;
  }

  return <Navigate to="/login/user-login" replace />;
}

const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};
const AppContent = () => {
  const location = useLocation(); // Get current route location
  const navigate = useNavigate();

  useEffect(() => {
    // Get the token from localStorage
    const token = getToken();
    const loginPage = decodeToken(token, 'loginPage');
    const isEnabled2FA = decodeToken(token, 'isEnabled2FA');
    const isVerified2FA = decodeToken(token, 'is2FaVerified');
    const is2FAForce = decodeToken(token, 'is2FAForce');

    const logoUrl = decodeToken(token, 'logo');

    const currentPage = location.pathname;
    if (token) {
      // Listen for real-time notifications
      socket.on('receive-notification', (data) => {
        console.log('🔔 New Notification:', data);
        alert('🔔 New Notification:');
        // You can show a toast or update state/UI here
      });

      // Cleanup on unmount
      return () => {
        socket.off('receive-notification');
      };
    }
    // console.log('currentPage:', currentPage);
    if (
      !token &&
      !currentPage.includes('/login/') &&
      !currentPage.includes('/portal/') &&
      !currentPage.includes('/forgot-') &&
      !currentPage.includes('/reset-') &&
      !currentPage.includes('/verify-') &&
      !currentPage.includes('-2fa') &&
      !currentPage.includes('/login-documentation') &&
      !currentPage.includes('/web') &&
      !currentPage === '/' &&
      !currentPage.includes('/registration/international-ngo-form') &&
      !currentPage.includes('/portal-promo')
    ) {
      // console.log('no token:', token);
      navigate(`/login/user-login`); // Redirect to login page
      // navigate('/')
    }
    // Check if token exists and if it's expired
    if (token && isTokenExpired(token)) {
      // If token expired, remove it from localStorage and redirect to login page
      removeToken();
      //   console.log('expired:');

      navigate(`/login/${loginPage}`); // Redirect to login page
    }
    // Check if is Enabled 2FA and is not Verified 2FA
    if (isEnabled2FA && !isVerified2FA) {
      navigate(`/check-2fa`); // Redirect to check 2fa page
    }

    // if (is2FAForce && !isEnabled2FA) {
    //   // console.log('is2FAForce:',  is2FAForce);

    //   // Redirect to the dashboard
    //   navigate('/register-2fa');
    // }

    if (logoUrl) {
      const iconUrl = `${API_URL}/${logoUrl}`;
      updateFavicon(iconUrl);
    }
  }, [navigate]);

  // List of routes where we don't want to show the Navbar
  const hideNavbarRoutes = [
    '/',
    '/login/:loginRoute',
    '/forgot-password/:forgotRoute',
    '/print',
    '/print-activity',

    '/reset-password/:token',
    '/verify-email/:token',
    '/register-2fa',
    '/check-2fa',

    '/portal/:route',
    '/invoice/:route/:id',
    '/documentation/:_id/:type',
    '/sys-features/:_id/:type',

    '/report/balance-sheet',
    '/report/income-statement',
    '/login-documentation/:email',
    '/registration/international-ngo-form',
    '/web',
    '/portal-promo',
    '/api-documentation/:link_id',
    '/election/candidates',
    '/election/results',
  ];

  // Helper function to match routes with parameters
  const matchRoute = (path, pattern) => {
    const regex = new RegExp(`^${pattern.replace(/:[^\s/]+/g, '([^/]+)')}$`); // Replace :param with a regex group
    const match = path.match(regex);

    if (match) {
      // Create an object mapping parameter names to values
      const paramNames = (pattern.match(/:[^\s/]+/g) || []).map((param) =>
        param.slice(1)
      );
      const params = {};
      paramNames.forEach((param, index) => {
        params[param] = match[index + 1];
      });
      return params; // Return the matched parameters
    }

    return null;
  };

  // // Check if the current route should hide the Navbar
  // const shouldHideNavbar = hideNavbarRoutes.some((route) => matchRoute(location.pathname, route));

  // Check if the current route should hide the Navbar
  const shouldHideNavbar = hideNavbarRoutes.some((route) => {
    const params = matchRoute(location.pathname, route);
    if (params) {
      console.log(`Route matched: ${route}`);
      console.log('Matched parameters:', params);
      return true; // Matched route with parameters
    }
    return false; // No match found
  });

  // List of routes where we will want to show the Charts
  const showChartsRoutes = ['/dashboard', '/charts'];
  // Check if the current route should show the Chart
  const shouldShowCharts = showChartsRoutes.includes(location.pathname);

  return (
    <>
      {/* Conditionally render the Navbar */}
      {!shouldHideNavbar && <Navbar />}

      {/* Conditionally render the Chart */}
      {shouldShowCharts && <Charts />}
      {/* Content section */}
      {/* Your page content goes here */}
      <Routes>
        {/* <Route path="/" element={<Login />} /> */}
        <Route path="/" element={<StartupRedirect />} />

        <Route path="/login/:loginRoute" element={<Login />} />
        <Route
          path="/forgot-password/:forgotRoute"
          element={<ForgotPassword />}
        />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/register-2fa" element={<Register2Fa />} />
        <Route path="/check-2fa" element={<Check2FA />} />
        <Route
          path="/login-documentation/:email"
          element={<LoginDocumentation />}
        />

        <Route path="/profile" element={<UserProfile />} />
        <Route path="/documentation/:_id/:type" element={<Documentation />} />
        <Route path="/sys-features/:_id/:type" element={<SysFeatures />} />

        <Route path="/report/balance-sheet" element={<BalanceSheet />} />

        <Route
          path="/form-report/permission/:route"
          element={<UserPermission />}
        />

        <Route path="/portal/:route" element={<PortalForm />} />
        <Route path="/invoice/:route/:id" element={<InvoicePage />} />

        <Route path="/dashboad" element={<Home />} />
        <Route path="/print" element={<PrintTableWithHeader />} />
        <Route path="/print-activity" element={<ActivityReport />} />

        <Route path="/report/:route" element={<Report />} />
        <Route path="/form-report/:route" element={<ReportForm />} />
        <Route path="/activity-report/:route" element={<ReportForm />} />

        <Route path="/multi-entry/:route" element={<MultiEntryForm />} />

        <Route path="/report/ktc-sql" element={<SqlHome />} />
        <Route path="/csv-uploader/:route" element={<CSVUploader />} />

        <Route path="/report/income-statement" element={<IncomeStatement />} />

        <Route path="/web" element={<KaafiPayWeb />} />
        <Route path="/portal-promo" element={<PortalPromo />} />
        <Route path="/report/sql-updates" element={<SqlViewer />} />
        <Route
          path="/api-documentation/:link_id"
          element={<ApiDocumentation />}
        />
        <Route path="/election/candidates" element={<ElectionPage />} />
        <Route path="/election/results" element={<ElectionResults />} />
      </Routes>

      {/* Conditionally render the Navbar */}
      {!shouldHideNavbar && <Footer />}
    </>
  );
};

export default App;
